import type { StorefrontConfig } from '@commerce/tokens'
import type { IConfig } from '@commerce/core'

/**
 * The authored storefront config, projected onto the read view the ported
 * domain expects.
 *
 * Two vocabularies meet here. `@commerce/tokens` owns what a storefront
 * *authors* (`commerce.currencySymbol`, `commerce.priceFormat`, …);
 * `@commerce/core` owns what the promotion engine *reads*
 * (`options.currencySymbol`, `options.format`, …), a shape inherited from
 * cinnamon and deliberately left alone so the ported helpers keep compiling.
 * This function is the one place the two names are tied together, so nothing
 * downstream has to know both.
 *
 * `currency` and `currencySymbol` both get the symbol on purpose: four ported
 * helpers read `config?.options?.currency || config?.options?.currencySymbol`,
 * and filling only the second would leave the first branch permanently taking
 * an empty value.
 */
export const toPromotionConfig = ({ commerce, texts }: StorefrontConfig): IConfig => ({
  options: {
    currency: commerce.currencySymbol,
    currencySymbol: commerce.currencySymbol,
    currencyCode: commerce.currencyCode,
    currencyDisplay: commerce.priceDisplay,
    format: commerce.priceFormat,
    promotionPercentageFormat: commerce.promotionPercentageFormat,
    // The authoring half of hide-prices. The slice derives `hidePricesActive`
    // from it the moment session data lands; an absent rule resolves to false,
    // which leaves `commerce.hidePrices` as the only switch — correct for a
    // storefront that does not use the per-customer feature.
    hidePrices: commerce.hidePricesRule,
  },
  texts,
})
